import { ApolloClient, gql, HttpLink, InMemoryCache } from "@apollo/client";
import { GetPosts, Post } from "../models/Posts";

export class PostsService {
  client: ApolloClient<unknown>;

  constructor() {
    const httpLink = new HttpLink({
      uri: process.env.NEXT_PUBLIC_SANITY_GRAPHQL_API,
      fetchOptions: { next: { revalidate: 120 } },
    });
    this.client = new ApolloClient({
      cache: new InMemoryCache(),
      link: httpLink,
    });
  }

  getLatestPost = async (limit = 6, language = "en") => {
    const res = await this.client.query<{ allPost: GetPosts }>({
      query: gql`
        query GerAllPost {
          allPost(sort: { created_at: DESC }, limit: ${limit}, where:{
            isPublished: {eq: true}
            language: {eq: "${language}"}
          }) {
            _id
            isPublished
            title
            language
            slug {
              current
            }
            shortDescription
            mainImage {
              asset{
                url
              }
            }
            categories {
              name
            }
          }
        }
      `,
    });

    return res;
  };

  getAllPublishedPostsForSitemap = async (language: string) => {
    const pageSize = 100;
    const maxPages = 200;
    const all: Array<{
      _id: string;
      _createdAt: string | null;
      slug: { current: string | null } | null;
    }> = [];
    let offset = 0;

    for (let pageIdx = 0; pageIdx < maxPages; pageIdx++) {
      const res = await this.client.query<{
        allPost: Array<{
          _id: string;
          _createdAt: string | null;
          slug: { current: string | null } | null;
        }>;
      }>({
        query: gql`
          query SitemapPosts($language: String!, $offset: Int!, $limit: Int!) {
            allPost(
              sort: { _createdAt: DESC }
              offset: $offset
              limit: $limit
              where: { isPublished: { eq: true }, language: { eq: $language } }
            ) {
              _id
              _createdAt
              slug {
                current
              }
            }
          }
        `,
        variables: { language, offset, limit: pageSize },
      });

      const batch = res.data?.allPost ?? [];
      all.push(...batch);
      if (batch.length < pageSize) break;
      offset += pageSize;
    }

    return all;
  };

  getPostById = async (id: string) => {
    const res = await this.client.query<{ Post: Post }>({
      query: gql`
        query PostById($id: ID!) {
          Post(id: $id) {
            title
            slug {
              current
            }
            shortDescription
            categories {
              name
            }
            author {
              name
            }
            mainImage {
              asset {
                url
              }
            }
            author {
              name
              avatar {
                asset {
                  url
                }
              }
            }
            contentRaw
            _createdAt
          }
        }
      `,
      variables: { id },
    });

    return res;
  };

  getPostBySlug = async (slug: string, language: string) => {
    const res = await this.client.query<{ allPost: Post[] }>({
      query: gql`
        query PostBySlug($slug: String!, $language: String!) {
          allPost(
            where: {
              slug: { current: { eq: $slug } }
              language: { eq: $language }
              isPublished: { eq: true }
            }
            limit: 1
          ) {
            title
            slug {
              current
            }
            shortDescription
            categories {
              name
            }
            author {
              name
              avatar {
                asset {
                  url
                }
              }
            }
            mainImage {
              asset {
                url
              }
            }
            contentRaw
            _createdAt
            translationOf {
              slug {
                current
              }
            }
          }
        }
      `,
      variables: { slug, language },
    });

    const post = res.data?.allPost?.[0] as
      | (Post & {
          translationOf?: { slug?: { current?: string | null } | null } | null;
        })
      | undefined;
    if (!post) return null;

    const { translationOf, ...rest } = post;

    return {
      ...rest,
      translationSlug: translationOf?.slug?.current ?? null,
    };
  };
}
